import { format } from 'date-fns';
import type {
  AccountModel,
  BoPhan,
  ChiTietKheUocLaiSuat,
  ChiTietKheUocThanhToan,
  HopDong,
  KhachHang,
  KheUoc
} from '@/types/schemas';
import type { DataTableColumn, DataTableRenderCellParams } from '@/components/custom/erp';
import { formatDate, formatMoney } from '@/lib/formatUtils';

// Cell renderer for loai_ku column
const LoanTypeCellRenderer = (params: DataTableRenderCellParams<KheUoc>) => {
  if (params.value === 1) return <span>Vay</span>;
  if (params.value === 2) return <span>Cho vay</span>;
  if (params.value === 3) return <span>Tín dụng</span>;
  return params.value;
};

export const Columns: DataTableColumn<KheUoc>[] = [
  { field: 'ma_ku', headerName: 'Mã khế ước', width: 120 },
  { field: 'ten_ku', headerName: 'Tên khế ước', width: 150 },
  { field: 'ten_ku2', headerName: 'Tên khác', width: 150 },
  {
    field: 'loai_ku',
    headerName: 'Loại khế ước',
    width: 120,
    renderCell: LoanTypeCellRenderer
  },
  {
    field: 'ma_kh',
    headerName: 'Mã khách hàng',
    width: 150,
    renderCell: (params: DataTableRenderCellParams<KheUoc>) => params.row.ma_kh_data?.customer_code
  },
  {
    field: 'ten_kh',
    headerName: 'Tên khách hàng',
    width: 200,
    renderCell: (params: DataTableRenderCellParams<KheUoc>) => params.row.ma_kh_data?.customer_name
  },
  {
    field: 'ngay_ku',
    headerName: 'Ngày ký',
    width: 120,
    align: 'center',
    renderCell: (params: DataTableRenderCellParams<KheUoc>) => formatDate(params.value)
  },
  {
    field: 'ngay_ku1',
    headerName: 'Ngày vay',
    align: 'center',
    width: 120,
    renderCell: (params: DataTableRenderCellParams<KheUoc>) => formatDate(params.value)
  },
  {
    field: 'ngay_ku2',
    headerName: 'Ngày đáo hạn',
    align: 'center',
    width: 120,
    renderCell: (params: DataTableRenderCellParams<KheUoc>) => formatDate(params.value)
  },
  {
    field: 'tien',
    headerName: 'Tiền vay',
    width: 140,
    type: 'number',
    renderCell: (params: DataTableRenderCellParams<KheUoc>) => formatMoney(params.value)
  },
  {
    field: 'tk',
    headerName: 'Tk hạch toán',
    width: 120,
    renderCell: (params: DataTableRenderCellParams<KheUoc>) => params.row.tk_data?.code
  },
  {
    field: 'status',
    headerName: 'Trạng thái',
    width: 120,
    renderCell: (params: DataTableRenderCellParams<KheUoc>) =>
      Number(params.value) === 1 ? '1. Còn sử dụng' : '0. Không sử dụng'
  }
];

export const InterestColumns: DataTableColumn<ChiTietKheUocLaiSuat>[] = [
  {
    field: 'ls',
    headerName: 'Lãi suất (%)',
    width: 120,
    valueFormatter: (params: { value?: string | number | null }) => params.value?.toLocaleString('vi-VN')
  },
  {
    field: 'ngay_hl',
    headerName: 'Hiệu lực từ',
    width: 150,
    valueFormatter: (params: { value?: string | null }) => formatDate(params.value)
  },
  { field: 'ghi_chu', headerName: 'Ghi chú', width: 300 }
];

export const paymentColumns: DataTableColumn<ChiTietKheUocThanhToan>[] = [
  {
    field: 'ngay_tt',
    headerName: 'Ngày thanh toán',
    width: 150,
    valueFormatter: (params: { value?: string | null }) => formatDate(params.value)
  },
  {
    field: 'tt',
    headerName: 'Tiền thanh toán',
    width: 150,
    valueFormatter: (params: { value?: string | number | null }) => params.value?.toLocaleString('vi-VN')
  },
  {
    field: 'tt_nt',
    headerName: 'Tiền thanh toán NT',
    width: 150,
    valueFormatter: (params: { value?: string | number | null }) => params.value?.toLocaleString('vi-VN')
  },
  { field: 'ghi_chu', headerName: 'Ghi chú', width: 250 }
];

export const getContractColumns: DataTableColumn<HopDong>[] = [
  {
    field: 'ma_hd',
    headerName: 'Mã hợp đồng',
    width: 200
  },
  {
    field: 'ten_hd',
    headerName: 'Tên hợp đồng',
    width: 320
  },
  {
    field: 'ngay_hd',
    headerName: 'Ngày hợp đồng',
    width: 150
  }
];
export const getDepartmentColumn: DataTableColumn<BoPhan>[] = [
  {
    field: 'ma_bp',
    headerName: 'Mã bộ phận',
    width: 200
  },
  {
    field: 'ten_bp',
    headerName: 'Tên bộ phận',
    width: 320
  },
  {
    field: 'ghi_chu',
    headerName: 'Ghi chú',
    width: 250
  }
];
export const getPartnerColumns: DataTableColumn<KhachHang>[] = [
  { field: 'customer_code', headerName: 'Mã khách hàng', width: 120 },
  { field: 'customer_name', headerName: 'Tên khách hàng', width: 280 },
  { field: 'receivable', headerName: 'Công nợ p/thu', width: 120 },
  { field: 'payable', headerName: 'Công nợ p/trả', width: 120 },
  { field: 'tax_code', headerName: 'Mã số thuế', width: 120 },
  { field: 'email', headerName: 'Email', width: 160 },
  { field: 'phone', headerName: 'Số điện thoại', width: 160 }
];

export const accountColumns: DataTableColumn<AccountModel>[] = [
  {
    field: 'code',
    headerName: 'Mã tài khoản',
    width: 120
  },
  {
    field: 'name',
    headerName: 'Tên tài khoản',
    width: 200
  },
  {
    field: 'parent_account_code',
    headerName: 'Tài khoản mẹ',
    width: 150,
    renderCell: (params: { row: AccountModel }) => {
      return params.row?.parent_account_code_data?.code;
    }
  },
  {
    field: 'tk_so_cai',
    headerName: 'Tk sổ cái',
    width: 130,
    checkboxSelection: true
  },
  {
    field: 'tk_chi_tiet',
    headerName: 'Tk chi tiết',
    width: 130,
    checkboxSelection: true
  },
  {
    field: 'bac_tk',
    headerName: 'Bậc tk',
    width: 100
  }
];
